import * as React from "react";
import {none, option, Option} from "ts-option";
import {RootState} from "../../../store/reducer/root/RootState";
import {connect} from "react-redux";
import JobTags from "../tag/JobTags";
import Job from "../../../model/job/Job";
import Loading from "../../ui/loading/Loading";
import UserDataDisplayer from "../../user/UserDataDisplayer";
import JobSiteAddress from "../../lead/v4/JobSiteAddress";
import JobContact from "../../lead/v4/JobContact";
import JobMerchantDropdown from "../merchant/JobMerchantDropdown";
import JobCompleteDate from "../completeDate/JobCompleteDate";
import JobDescription from "../../lead/v4/leadDescription/JobDescription";
import JobAssetsSerialNumbers from "../serialNumbers/JobAssetsSerialNumbers";
import JobWatchers from "../v4/JobWatchers";
import JobContactTitle from "../../contacts/JobContactTitle";
import {api} from "../../../api/api";
import {notifyBugsnagAndShowToast} from "../../../utils/Utils";
import JobDetailsModel from "../../../model/job/JobDetailsModel";
import {OnSuccess} from "../../../store/action/actionHelpers";
import JobPickupDatePresenter from "../date/JobPickupDatePresenter";
import JobInstallationDatePresenter from "../date/JobInstallationDatePresenter";
import PartsCollectionConfirmedDatePresenter from "../date/PartsCollectionConfirmedDatePresenter";
import BrandDisplayer from "../../brand/BrandDisplayer";
import PartnerBrandDisplayer from "../../partnerBrand/PartnerBrandDisplayer";
import CustomerSiteAddress from "../../lead/v4/CustomerSiteAddress";
import IndividualContact from "../../../model/contact/IndividualContact";
import PreferredMerchantBranchLocationDisplayer
	from "../../admin/partnerBrands/merchant/PreferredMerchantBranchLocationDisplayer";
import CustomFieldValue from "../../../model/job/CustomFieldValue";
import * as _ from "underscore";
import JobCustomFields from "../customFields/JobCustomFields";
import {JobSidebarItem} from "../../admin/sidebarConfig/JobSidebarConfig";
import SelectJobMerchantBranchLocation from "../../admin/partnerBrands/merchant/SelectJobMerchantBranchLocation";
import JobTypeDisplayer from "../jobType/JobTypeDisplayer";
import JobInvoicePathDropdown from "../invoicePath/JobInvoicePathDropdown";
import JobWarrantyRegistrationStatusDropDown from "../warrantyRegistration/JobWarrantyRegistrationStatusDropDown";

interface ComponentProps {
	/**
	 * Job.
	 */
	job: Option<Job>;
	/**
	 * Contact.
	 */
	contact: Option<IndividualContact>;
}

interface ReduxProps {
	/**
	 * Job sidebar order.
	 */
	jobSidebarOrder: Option<string[]>;
}

interface State {
	/**
	 * Job details
	 */
	jobDetails: Option<JobDetailsModel>;
	/**
	 * Job custom fields values
	 */
	jobCustomFieldsValues: Option<CustomFieldValue[]>;
}

type Props = ComponentProps & ReduxProps;

class JobSidebar extends React.Component<Props, State> {
	sidebarMap: { [key in JobSidebarItem]?: any } = {
		JOB_CREATOR: UserDataDisplayer,
		SITE_ADDRESS: JobSiteAddress,
		CUSTOMER: JobContact,
		MERCHANT_BRANCH_LOCATION: PreferredMerchantBranchLocationDisplayer,
		MERCHANT: JobMerchantDropdown,
		MERCHANT_BRANCH_DROPDOWN: SelectJobMerchantBranchLocation,
		JOB_COMPLETE_DATE: JobCompleteDate,
		TAGS: JobTags,
		DESCRIPTION: JobDescription,
		TITLE: JobContactTitle,
		ASSETS_SERIAL_NUMBERS: JobAssetsSerialNumbers,
		WARRANTY_REGISTRATION_STATUS: JobWarrantyRegistrationStatusDropDown,
		JOB_TYPE: JobTypeDisplayer,
		WATCHERS: JobWatchers,
		JOB_INVOICE_PATH: JobInvoicePathDropdown
	};

	sidebarJobDetailsMap: { [key in JobSidebarItem]?: any } = {
		PICKUP_DATE: JobPickupDatePresenter,
		INSTALLATION_DATE: JobInstallationDatePresenter,
		COLLECTION_CONFIRMED: PartsCollectionConfirmedDatePresenter
	};

	sidebarContactDetailsMap: { [key in JobSidebarItem]?: any } = {
		BRAND: BrandDisplayer,
		PARTNER_BRAND: PartnerBrandDisplayer,
		CUSTOMER_ADDRESS: CustomerSiteAddress
	};

	sidebarCustomFieldsDetailsMap: { [key in JobSidebarItem]?: any } = {
		CUSTOM_FIELDS: JobCustomFields
	};

	constructor(props: Props) {
		super(props);
		this.state = {jobDetails: none, jobCustomFieldsValues: none};
	}

	componentDidMount() {
		this.props.job.map(j => {
			this.fetchJobDetails(j.id);
			this.fetchJobCustomFieldsValues(j.id);
		});
	}

	componentWillReceiveProps(nextProps: Props) {
		if (this.props.job.map(j => j.id).orNull !== nextProps.job.map(j => j.id).orNull) {
			nextProps.job.map(j => {
				this.fetchJobDetails(j.id);
				this.fetchJobCustomFieldsValues(j.id);
			});
		}
	}

	render() {
		return (
			<Loading isLoading={this.props.jobSidebarOrder.isEmpty || this.props.job.isEmpty}>
				{this.props.jobSidebarOrder.getOrElse([]).map(this.renderSidebarDetails)}
			</Loading>
		);
	}

	renderSidebarDetails = (item: JobSidebarItem): JSX.Element => {
		if (this.sidebarMap[item]) {
			return this.renderJobComponent(item);
		}
		else if (this.sidebarJobDetailsMap[item]) {
			return this.renderJobDetailsComponent(item);
		}
		else if (this.sidebarContactDetailsMap[item]) {
			return this.renderContactComponent(item);
		}
		else if (this.sidebarCustomFieldsDetailsMap[item]) {
			return this.renderCustomFieldComponent(item);
		}
	};

	renderJobComponent = (item: JobSidebarItem): JSX.Element => {
		const Component = this.sidebarMap[item];
		if (item === JobSidebarItem.MERCHANT_BRANCH_LOCATION) {
			return <Component item={this.props.job} key={item}/>;
		}
		return <Component job={this.props.job} key={item}/>;
	};

	renderJobDetailsComponent = (item: JobSidebarItem): JSX.Element => {
		const Component = this.sidebarJobDetailsMap[item];
		return <Component jobDetails={this.state.jobDetails} editJobDetails={this.updateJobDetails} key={item}/>;
	};

	renderContactComponent = (item: JobSidebarItem): JSX.Element => {
		const Component = this.sidebarContactDetailsMap[item];
		if (item === JobSidebarItem.PARTNER_BRAND) {
			return <Component contact={this.props.contact} displayAutoPayout={true} key={item}/>;
		}
		return <Component contact={this.props.contact} key={item}/>;
	};

	renderCustomFieldComponent = (item: JobSidebarItem): JSX.Element => {
		const Component = this.sidebarCustomFieldsDetailsMap[item];
		return <Component jobCustomFieldsValues={this.state.jobCustomFieldsValues}
						  createJobCustomFieldValue={this.createJobCustomFieldValue}
						  updateJobCustomFieldValue={this.updateJobCustomFieldValue}
						  removeJobCustomFieldValue={this.removeJobCustomFieldValue}
						  key={item}/>;
	};

	fetchJobDetails = (jobId: string) => {
		this.setState({jobDetails: none}, () => api.instance.jobApi.fetchJobDetails(jobId)
			.then(jobDetails => this.setState({jobDetails: option(jobDetails)}))
			.catch(err => notifyBugsnagAndShowToast(err, "Fetch job details")))
	};

	updateJobDetails = (jobDetails: JobDetailsModel, onSuccess: OnSuccess) => {
		this.props.job.map(job => api.instance.jobApi.updateJobDetails(job.id, jobDetails)
			.then(jobDetails => this.setState({jobDetails: option(jobDetails)}, onSuccess))
			.catch(err => notifyBugsnagAndShowToast(err, "Update job details")))
	};

	fetchJobCustomFieldsValues = (jobId: string) => {
		this.setState({jobCustomFieldsValues: none}, () => api.instance.jobApi.fetchJobCustomFieldsValues(jobId)
			.then(jobCustomFieldsValues => this.setState({jobCustomFieldsValues: option(jobCustomFieldsValues)}))
			.catch(err => notifyBugsnagAndShowToast(err, "Fetch job custom fields values")))
	};

	createJobCustomFieldValue = (createdCustomFieldValue: CustomFieldValue, onSuccess: OnSuccess) => {
		this.props.job.map(job => api.instance.jobApi.createJobCustomFieldValue(job.id, createdCustomFieldValue)
			.then((customFieldValue) => {
				let customFieldsValues = this.state.jobCustomFieldsValues.getOrElse([]);
				customFieldsValues.push(customFieldValue);
				this.setState({jobCustomFieldsValues: option(customFieldsValues)}, onSuccess);
			})
			.catch(err => notifyBugsnagAndShowToast(err, "Create job custom field value")));
	};

	updateJobCustomFieldValue = (updatedCustomFieldValue: CustomFieldValue, onSuccess: OnSuccess) => {
		api.instance.jobApi.updateJobCustomFieldValue(updatedCustomFieldValue)
			.then((customFieldValue) => {
				let customFieldsValues = this.state.jobCustomFieldsValues.getOrElse([]);
				const updatedIndex = _.findIndex(customFieldsValues,
					(item) => item.customFieldId === customFieldValue.customFieldId);
				if (updatedIndex > -1) {
					customFieldsValues[updatedIndex] = customFieldValue;
					this.setState({jobCustomFieldsValues: option(customFieldsValues)}, onSuccess);
				}
			})
			.catch(err => notifyBugsnagAndShowToast(err, "Update job custom field value"));
	};

	removeJobCustomFieldValue = (customFieldValueId: string, onSuccess: OnSuccess) => {
		api.instance.jobApi.deleteJobCustomFieldValue(customFieldValueId)
			.then(() => {
				let customFieldsValues = this.state.jobCustomFieldsValues.getOrElse([]);
				const deletedIndex = _.findIndex(customFieldsValues, (item) => item.id.get === customFieldValueId);
				if (deletedIndex > -1) {
					customFieldsValues.splice(deletedIndex, 1);
					this.setState({jobCustomFieldsValues: option(customFieldsValues)}, onSuccess);
				}
			})
			.catch(err => notifyBugsnagAndShowToast(err, "Delete job custom field value"));
	};
}

function mapStateToProps(state: RootState) {
	return {
		jobSidebarOrder: state.sidebarOrder.jobSidebarOrder
	}
}

export default connect(mapStateToProps)(JobSidebar)
